import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { X } from 'lucide-react'
import ChangeFeed from './ChangeFeed'
import TeamScoreChart from './TeamScoreChart'
import { RosterPanel, TierMix } from './TeamRoster'
import { teamChanges, useTeamRosters, valueAt } from './data'
import { RankDelta, ScoreDelta, TeamLogo, formatDay, formatScore } from './ui'

function Stat({ label, children, hint }) {
  return (
    <div className="hv-stat">
      <span className="hv-stat-label">{label}</span>
      <span className="hv-stat-value">{children}</span>
      {hint && <span className="hv-stat-hint">{hint}</span>}
    </div>
  )
}

export default function TeamStory({ league, team, dates, pinnedDate, onPinDate, onClose }) {
  const { snapshots, loading } = useTeamRosters(team.id)
  const changes = useMemo(() => teamChanges(league, team, dates), [league, team, dates])

  const stats = useMemo(() => {
    const values = dates.map(d => ({ date: d, ...valueAt(team, d, league) })).filter(v => v.rank)
    const first = values[0]
    const last = values.at(-1)
    const best = values.reduce((a, b) => (b.rank < a.rank ? b : a), first)
    const worst = values.reduce((a, b) => (b.rank > a.rank ? b : a), first)
    const events = changes.flatMap(c => c.events)
    return {
      first,
      last,
      best,
      worst,
      roster: events.filter(e => e.kind === 'in' || e.kind === 'out').length,
      tiers: events.filter(e => e.kind === 'tier').length,
    }
  }, [league, team, dates, changes])

  const shownDate = pinnedDate && pinnedDate >= dates[0] ? pinnedDate : dates.at(-1)

  return (
    <motion.section
      key={team.id}
      className="hv-story"
      style={{ '--team': team.accent }}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      <div className="hv-card hv-hero">
        <div className="hv-hero-glow" aria-hidden="true" />
        <div className="hv-hero-id">
          <TeamLogo team={team} size={72} />
          <div>
            <p className="hv-eyebrow">Team story</p>
            <h2>{team.name}</h2>
            <p className="hv-muted hv-small">
              {formatDay(dates[0])} – {formatDay(dates.at(-1))}
            </p>
          </div>
          <button className="hv-icon-btn hv-hero-close" onClick={onClose} aria-label="Back to the whole league">
            <X size={18} />
          </button>
        </div>
        {stats.last && (
          <div className="hv-stats">
            <Stat label="Rank now" hint={<>from #{stats.first.rank}</>}>
              #{stats.last.rank} <RankDelta value={stats.first.rank - stats.last.rank} size="lg" />
            </Stat>
            <Stat label="Score" hint={<>from {formatScore(stats.first.score)}</>}>
              {formatScore(stats.last.score)} <ScoreDelta value={stats.last.score - stats.first.score} />
            </Stat>
            <Stat label="Best rank" hint={formatDay(stats.best.date)}>#{stats.best.rank}</Stat>
            <Stat label="Worst rank" hint={formatDay(stats.worst.date)}>#{stats.worst.rank}</Stat>
            <Stat label="Roster moves">{stats.roster}</Stat>
            <Stat label="Re-rankings">{stats.tiers}</Stat>
          </div>
        )}
      </div>

      <div className="hv-card">
        <header className="hv-card-head">
          <div>
            <h3>Score over time</h3>
            <p className="hv-muted hv-small">Dots mark updates with roster or ranking changes. Click anywhere to look at that day.</p>
          </div>
        </header>
        <TeamScoreChart league={league} team={team} dates={dates} pinnedDate={pinnedDate} onPinDate={onPinDate} />
      </div>

      <div className="hv-story-grid">
        <ChangeFeed league={league} changes={changes} pinnedDate={pinnedDate} onPinDate={onPinDate} />
        <div className="hv-story-side">
          <RosterPanel snapshots={snapshots} loading={loading} date={shownDate} trackedSince={league.trackedSince} />
          <TierMix snapshots={snapshots} dates={dates} pinnedDate={pinnedDate} onPinDate={onPinDate} />
        </div>
      </div>
    </motion.section>
  )
}
